import { DEFAULT_TERRITORY_COLOR } from '@user/store/territory/constants'
import { APIError, isAPIValidationError } from '@user/store/services/crud'
import { FormikHelpers } from 'formik'
import { useCallback, useState } from 'react'
import { toast } from '@sales-center/components'
import {
  LeadAssignmentRuleFormModel,
  LeadAssignmentRuleFormProps,
} from '../typings'
import {
  createAssignmentRule,
  updateAssignmentRule,
} from '@user/store/leadAssignmentRule'
import { useAppDispatch } from '@user/store/hooks'
import { defaultGUIDValue } from '@user/components/Dashboard/widgets/utils/constants'
import { getTouchedObject } from '@sales-center/common'
import { trimString } from '@sales-center/common'
import { parseCoverageError } from '../../utils/coverageError'
import { createTerritory } from '@user/store/territory/api'
import {
  findDraftedTerritory,
  replaceDraftedTerritory,
} from '@user/components/Settings/LeadAssignmentRule/territoryConditionDraft'

export function useOnSubmit(props: LeadAssignmentRuleFormProps) {
  const { onClose } = props
  const dispatch = useAppDispatch()
  const [coverageBlockLabels, setCoverageBlockLabels] = useState<
    string[] | null
  >(null)

  const closeCoverageBlock = useCallback(() => setCoverageBlockLabels(null), [])

  const onSubmit = useCallback(
    async (
      values: LeadAssignmentRuleFormModel,
      helpers: FormikHelpers<LeadAssignmentRuleFormModel>,
      id?: number,
    ) => {
      try {
        // A territory drawn for this rule is created HERE, not when it was drawn (WWM-17508).
        // The server requires a sales team on a territory and the team is this form's to give,
        // so the shape waits until the rule is saved -- which also means abandoning the form
        // leaves nothing behind.
        //
        // No owners are sent: routing is the rule's job now, and the server rejects an owner
        // who is not a member of the chosen team, which a rule spanning several teams could
        // easily produce. The territory's own owners stay empty and the rule decides.
        const drafted = findDraftedTerritory(values.assignmentRuleConditions)
        let conditionsWithTerritory = values.assignmentRuleConditions

        if (drafted) {
          const salesTeamId = values.salesTeamIds?.[0]

          if (!salesTeamId) {
            // Cannot happen through the form -- a sales team is required -- but sending a
            // territory with no team would 400 with a message about a field the admin never
            // saw, so it is said plainly here instead.
            helpers.setErrors({
              salesTeamIds:
                'Choose a sales team before saving the new territory.',
            })
            helpers.setTouched(
              getTouchedObject({ salesTeamIds: 'required' }),
              false,
            )
            return
          }

          const created = await dispatch(
            createTerritory({
              name: drafted.name,
              salesTeamId,
              ownerIds: [],
              colorHex: DEFAULT_TERRITORY_COLOR,
              boundary: drafted.boundary,
            }),
          )

          if (!createTerritory.fulfilled.match(created)) {
            const reason =
              (created.payload as { message?: string } | undefined)?.message ??
              'Could not save the territory you drew. Please try again.'
            toast.error(reason)
            return
          }

          conditionsWithTerritory = replaceDraftedTerritory(
            values.assignmentRuleConditions,
            created.payload.id,
          )

          // Written back into the form, not just held in this local, because the territory is
          // already saved and this submit can still fail after it: a server validation error on
          // the rule is routed back into the form below for the user to correct. Left in `values`,
          // the drafted-territory sentinel would survive that, and the next submit would POST a
          // second territory with the same name -- which the server refuses, so the rule could
          // never be saved at all without redrawing under a different name, and the first
          // territory would be stranded on the map with no rule pointing at it.
          //
          // shouldValidate false: this is a resolved id replacing a placeholder, not something the
          // user typed, and validating mid-submit would surface errors for fields they have not
          // reached yet.
          helpers.setFieldValue(
            'assignmentRuleConditions',
            conditionsWithTerritory,
            false,
          )
        }

        const assignmentRuleConditions = conditionsWithTerritory.map(
          (condition) => ({
            ...condition,
            assignmentRuleConditionValues:
              condition.assignmentRuleConditionValues.map(
                // Optional on BOTH accesses. Condition values are rehydrated by options.find(),
                // which yields undefined when a stored id is not among the options -- and for a
                // Territory condition the options are empty whenever the D2D flag is off, since
                // useGetTerritoriesOnMount no-ops. Opening an existing rule on a tenant whose flag
                // was switched off then threw here instead of showing the rule.
                (value) => value?.id || value?.abbreviation || value,
              ),
          }),
        )

        const description = values.description
          ? values.description.replace(/\n/g, ' ').trim()
          : ''

        const name = trimString(values.name)

        const resultInput = {
          ...values,
          assignmentRuleConditions,
          description,
          name,
        }

        if (!values.salesFunnelId) resultInput.leadStatusId = ''

        if (values.ownerIds.length === 0) {
          resultInput.ownerIds = [defaultGUIDValue]
        }
        const result = id
          ? await dispatch(updateAssignmentRule(resultInput))
          : await dispatch(createAssignmentRule(resultInput))

        if (
          createAssignmentRule.fulfilled.match(result) ||
          updateAssignmentRule.fulfilled.match(result)
        ) {
          toast.success(`Assignment rule ${id ? 'updated' : 'created'}`)
          helpers.resetForm()
          onClose()
        } else {
          const { payload } = result
          if (payload) {
            if (isAPIValidationError(payload)) {
              const { errors } = payload

              if (errors) {
                const errorsObject = {
                  matchType: errors['MatchType']?.join(', '),
                  name: errors['Name']?.join(', '),
                  ownerIds: errors['OwnerIds']?.join(', '),
                  salesTeamIds: errors['SalesTeamIds']?.join(', '),
                  salesFunnelId: errors['SalesFunnelId']?.join(', '),
                  leadStatusId: errors['LeadStatusId']?.join(', '),
                  assignmentRuleConditions:
                    errors['AssignmentRuleConditions']?.join(', '),
                }

                helpers.setErrors(errorsObject)
                helpers.setTouched(getTouchedObject(errorsObject), false)
                helpers.setStatus('APIError')
              }
            } else {
              const labels = parseCoverageError(payload)
              if (labels) {
                setCoverageBlockLabels(labels)
              } else {
                const { Message } = payload as APIError
                toast.error(Message)
              }
            }
          } else {
            toast.error('Unexpected error occurred.')
          }
        }
      } catch (e) {
        toast.error('Unexpected error occurred.')
      }
    },
    [dispatch, onClose],
  )

  return { onSubmit, coverageBlockLabels, closeCoverageBlock }
}
